import {
  ArrowsClockwise,
  Files,
  Paperclip,
  Warning,
} from "@phosphor-icons/react";

import { Button } from "common/components/ui/Button";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import type { JSX } from "react";

interface FileInfoProps {
  name: string;
  rowsCount: number;
  missingData: Record<string, unknown>[];
  isFileError: boolean;
  isInstantly: boolean;
  changeFile: () => void;
}

function exportFailedRowsToCSV(failedRows: Record<string, unknown>[]) {
  const columns = Object.keys(failedRows[0]);

  let csvContent = `${columns.join(",")}\n`;

  failedRows.forEach((failedRow) => {
    const row = columns
      .map((column) => {
        const value = failedRow[column];

        if (value === null || value === undefined) {
          return "";
        }

        // Convert value to string and escape quotes by doubling them
        const stringValue = value.toString().replace(/"/g, '""');

        // Wrap row in quotes to handle commas
        return `"${stringValue}"`;
      })
      .join(",");

    csvContent += `${row}\n`;
  });

  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const downloadUrl = URL.createObjectURL(blob);

  return downloadUrl;
}

export default function FileInfo({
  name,
  missingData,
  rowsCount,
  isFileError,
  isInstantly,
  changeFile,
}: FileInfoProps) {
  const isDesktop = useTwBreakpoint("lg");

  const missingDataCount = missingData.length;

  let errorContent: JSX.Element;
  if (isFileError) {
    errorContent = (
      <div className="flex items-center gap-x-0.5 text-red-500">
        <Warning weight="fill" size={16} />

        <p className="text-xs">
          Uploaded file must have valid column names and a valid LinkedIn URL in
          the first row
        </p>
      </div>
    );
  } else if (missingDataCount) {
    errorContent = (
      <div className="flex items-center gap-x-0.5 text-yellow-500">
        <Warning weight="fill" size={16} />

        <p className="text-xs">
          {missingDataCount}/{rowsCount} rows have missing data, make sure to
          add fallbacks for all variables.{" "}
          <a
            href={exportFailedRowsToCSV(missingData)}
            download="FailedRows.csv"
            className="text-xs-bold underline"
          >
            Export rows
          </a>
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="flex items-center justify-between gap-x-2.5 lg:gap-x-3 border border-black-200 p-3 rounded-3xl">
        <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-3xl bg-purple-50">
          {isInstantly ? (
            <Files size={24} className="fill-purple-600" />
          ) : (
            <Paperclip size={24} className="fill-purple-600" />
          )}
        </span>

        <div className="flex flex-1 flex-col lg:gap-y-1">
          <h3 className="break-all text-left text-md-bold lg:text-lg-bold">
            {name}
          </h3>
          <span className="text-black-500">
            {rowsCount} row{rowsCount > 1 ? "s" : ""}
          </span>
        </div>

        <Button
          variant="secondary-black"
          leftIcon={<ArrowsClockwise />}
          onClick={changeFile}
          size="sm"
        >
          Change{isDesktop ? ` ${isInstantly ? "list" : "file"}` : ""}
        </Button>
      </div>

      {errorContent}
    </>
  );
}
